import { useMemo } from 'react';
import { type AthleteStatusResponse } from 'processing-status/activity-status-util';
import { ActivityAthlete } from 'oas';

export interface AthleteStatusGroups {
    liveCompleted: ActivityAthlete[];
    liveInProgress: ActivityAthlete[];
    liveError: ActivityAthlete[];
    mungedCompleted: ActivityAthlete[];
    mungedInProgress: ActivityAthlete[];
    mungedError: ActivityAthlete[];
    //
    liveAll: ActivityAthlete[];
    mungedAll: ActivityAthlete[];
    completedAll: ActivityAthlete[];
    processingAll: ActivityAthlete[];
    errorAll: ActivityAthlete[];
    //
    missing: ActivityAthlete[];
    // status objects that didn't match a mapped athlete
    unmapped: AthleteStatusResponse[];
}

/**
 * Custom hook to filter and group mapped athletes based on their processing status.
 * Also detects missing athletes, and unmapped athlete statuses.
 */

export const useAthleteStatusGroups = (
    activityAthletes: ActivityAthlete[] | undefined,
    athleteStatuses: AthleteStatusResponse[] | undefined,
): AthleteStatusGroups => {
    return useMemo(() => {
        const athletes = Array.isArray(activityAthletes) ? activityAthletes : [];
        const statuses = Array.isArray(athleteStatuses) ? athleteStatuses : [];

        const groups: AthleteStatusGroups = {
            // combined dataSource and status
            liveCompleted: [],
            liveInProgress: [],
            liveError: [],
            mungedCompleted: [],
            mungedInProgress: [],
            mungedError: [],
            // by dataSource only
            liveAll: [],
            mungedAll: [],
            // by status only
            completedAll: [],
            processingAll: [],
            errorAll: [],
            // computed
            missing: [],
            // athlete statuses with no corresponding athlete in activity
            unmapped: [],
        };

        if (statuses.length === 0) {
            return groups;
        }

        const mungedMap = new Map(
            statuses.filter((s) => s.dataSource === 'Munged').map((s) => [s.athleteId, s.status]),
        );

        const athletesMap = new Map<string, ActivityAthlete>();

        athletes.forEach((athlete) => {
            // populate the map
            athletesMap.set(athlete.id, athlete);

            // an athlete is missing if: it has no Munged status, or Munged status is 'failed'
            const mungedStatus = mungedMap.get(athlete.id);
            if (!mungedStatus || mungedStatus === 'Failed') {
                groups.missing.push(athlete);
            }
        });

        statuses.forEach((status: AthleteStatusResponse) => {
            const athlete = athletesMap.get(status.athleteId);

            if (!athlete) {
                groups.unmapped.push(status);
                return;
            }

            // Combined Groups (dataSource + status)
            if (status.dataSource === 'Live') {
                if (status.status === 'Completed') {
                    groups.liveCompleted.push(athlete);
                }
                if (status.status === 'InProgress') {
                    groups.liveInProgress.push(athlete);
                }
                if (status.status === 'Failed') {
                    groups.liveError.push(athlete);
                }
            }

            if (status.dataSource === 'Munged') {
                if (status.status === 'Completed') {
                    groups.mungedCompleted.push(athlete);
                }
                if (status.status === 'InProgress') {
                    groups.mungedInProgress.push(athlete);
                }
                if (status.status === 'Failed') {
                    groups.mungedError.push(athlete);
                }
            }

            // Single Groups (by dataSource or status)

            // By dataSource
            if (status.dataSource === 'Live') {
                groups.liveAll.push(athlete);
            }
            if (status.dataSource === 'Munged') {
                groups.mungedAll.push(athlete);
            }

            // By status
            if (status.status === 'Completed') {
                groups.completedAll.push(athlete);
            }
            if (status.status === 'InProgress') {
                groups.processingAll.push(athlete);
            }
            if (status.status === 'Failed') {
                groups.errorAll.push(athlete);
            }
        });

        return groups;
    }, [athleteStatuses, activityAthletes]);
};
